/**
 * File:        resources/js/hooks/use-reduced-motion.ts
 * Module:      Design System · A11y
 * Purpose:     Returns true when the user has requested reduced motion at the OS level.
 *              Use to gate Framer Motion / Tailwind transition utilities in React components.
 *
 * Exports:
 *   - useReducedMotion() => boolean  — reactive value, updates on media-query change
 *
 * Depends on:
 *   - React (useSyncExternalStore, window.matchMedia)
 *
 * Side-effects:
 *   - subscribes/unsubscribes to matchMedia change events via useSyncExternalStore
 *
 * Key invariants:
 *   - SSR-safe: returns false on the server (no window).
 *   - Initial render reads mql.matches once — no synchronous setState inside an effect.
 *
 * Author:      Aman Vats Sharma
 * Last-updated: 2026-06-17
 */
import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(listener: () => void): () => void {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return () => {};
    }
    const mql = window.matchMedia(QUERY);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
}

function getSnapshot(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return false;
    }
    return window.matchMedia(QUERY).matches;
}

function getServerSnapshot(): boolean {
    return false;
}

export function useReducedMotion(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
